<template>
  <div :class="['showcase-article-card', `showcase-article-card--${props.variant}`]">
    <div class="showcase-article-card__hero">
      <img
        :src="props.article.image"
        :alt="props.article.title"
        class="showcase-article-card__hero-img"
        width="320"
        height="160"
        loading="lazy"
        decoding="async"
        @error="onHeroError" />
    </div>
    <div class="showcase-article-card__body">
      <h3 class="showcase-article-card__title">{{ props.article.title }}</h3>
      <div
        v-if="props.article.readingTimeLabel"
        class="showcase-article-card__meta">
        <span
          class="showcase-article-card__clock"
          aria-hidden="true"
          >🕐</span
        >
        <span class="showcase-article-card__read">{{ props.article.readingTimeLabel }}</span>
      </div>
      <NuxtLink
        v-if="props.article.link"
        :to="props.article.link"
        class="showcase-article-card__link">
        {{ readMoreLabel }} →
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ComputedRef, PropType } from 'vue'
import type { ShowcaseArticleCardData } from '~/types/homeShowcases'
import type {
  ShowcaseArticleCardProps,
  ShowcaseArticleCardVariant,
} from '~/types/ShowcaseArticleCard'
import { SHOWCASE_HERO_FALLBACK_BACKGROUND } from '~/utils/homeShowcases'

/**
 * Définit les props du composant ShowcaseArticleCard.
 */
const props: ShowcaseArticleCardProps = defineProps({
  article: {
    type: Object as PropType<ShowcaseArticleCardData>,
    required: true,
  },
  variant: {
    type: String as PropType<ShowcaseArticleCardVariant>,
    required: true,
  },
})

const { t } = useI18n()

const readMoreLabel: ComputedRef<string> = computed((): string => t('advice.readMore'))

/**
 * Applique le dégradé de secours sur le hero si l'image ne charge pas.
 * @param {Event} event - Événement `error` de l'image.
 */
function onHeroError(event: Event): void {
  const target: HTMLImageElement | null = event.target as HTMLImageElement | null
  const parent: HTMLElement | null = target?.parentElement ?? null
  if (!parent) return
  parent.style.background = SHOWCASE_HERO_FALLBACK_BACKGROUND
}
</script>

<style scoped>
.showcase-article-card {
  position: absolute;
  background: #1a1a1a;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  width: 280px;
  border: 1px solid rgba(249, 115, 22, 0.2);
  overflow: hidden;
}

.showcase-article-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, var(--accent-orange), #b45309);
}

.showcase-article-card--back {
  top: 0;
  left: 0;
  z-index: 1;
  transform: rotate(-1.5deg);
}

.showcase-article-card--front {
  top: 90px;
  right: 0;
  z-index: 2;
  transform: rotate(1deg);
}

.showcase-article-card__hero {
  height: 160px;
  background: #2a2a2a;
  overflow: hidden;
}

.showcase-article-card__hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.showcase-article-card__body {
  padding: var(--space-4) var(--space-4) var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.showcase-article-card__title {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-primary);
  line-height: var(--leading-snug);
}

.showcase-article-card__meta {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.showcase-article-card__clock {
  font-size: 0.75rem;
}

.showcase-article-card__read {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.showcase-article-card__link {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-muted);
  text-decoration: none;
  align-self: flex-end;
  transition: color var(--duration-fast) var(--ease-out);
}

.showcase-article-card__link:hover {
  color: var(--accent-orange);
}

@media (max-width: 900px) {
  .showcase-article-card {
    width: 240px;
  }

  .showcase-article-card--back {
    left: 0;
  }

  .showcase-article-card--front {
    right: 0;
    top: 70px;
  }
}

@media (max-width: 480px) {
  .showcase-article-card {
    width: 200px;
  }

  .showcase-article-card__hero {
    height: 120px;
  }
}
</style>
